/**
 * H22b: sect activities UI. A panel (opens when the sect senior talks, or the "สำนัก" menu button): activity buttons with
 * a target-sect picker for the spy mission, page counts per book with รวมเล่ม then อ่านเรียน. A small tracker card shows the
 * active run (waypoints / spies / scout points + detection). Also the "เข้าร่วมรำสำนัก" prompt while the sect dance is open.
 * Reads state, sends commands only. Frame = .ls-card.
 */
import {
  SECT_ACT_NAME,
  SECT_BOOKS,
  SECT_DANCE,
  SECT_TABLE,
  bookGoodId,
  pageId,
  sectDanceState,
  type PlayerCommand,
  type SectActKind,
  type World,
  type WorldEvent,
} from '@nine/shared';
import '../loading/loading.css';
import { $, h } from './dom';
import { cardClose, cardMenuButton } from './card';

interface Hud { kind: SectActKind; next: number; total: number; foes: number; caught: boolean }

const CSS = `
#sectAct.ls-card{position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);width:min(420px,calc(100vw - 20px));max-height:calc(100vh - 20px);overflow-y:auto;z-index:60;gap:6px;padding:16px 18px;font-size:14px}
#sectAct[hidden],#sectTrack[hidden],#sectDanceGo[hidden]{display:none}
#sectAct h3{margin:0;display:flex;justify-content:space-between;align-items:center;font-size:18px}
#sectAct button,#sectDanceGo button{min-height:34px;cursor:pointer}
#sectAct .row{display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin:2px 0}
#sectTrack.ls-card{position:fixed;left:10px;top:120px;z-index:40;padding:8px 12px;font-size:13px;pointer-events:none}
#sectDanceGo.ls-card{position:fixed;left:50%;bottom:140px;transform:translateX(-50%);z-index:45;padding:8px 14px;font-size:14px;text-align:center}
`;

export class SectActivitiesUi {
  private readonly panel = h('div', { class: 'ls-card', id: 'sectAct', hidden: true });
  private readonly track = h('div', { class: 'ls-card', id: 'sectTrack', hidden: true });
  private readonly dance = h('div', { class: 'ls-card', id: 'sectDanceGo', hidden: true });
  private target = '';
  private t = 0;
  private sig = '';

  constructor(
    private readonly world: World,
    private readonly send: (c: PlayerCommand) => void,
  ) {
    document.head.append(h('style', {}, CSS));
    const go = h('button', { type: 'button' }, 'เข้าร่วมรำสำนัก');
    go.addEventListener('click', () => this.send({ t: 'danceStart' }));
    this.dance.append(h('div', {}, 'ถึงเวลารำสำนัก'), go);
    for (const e of [this.panel, this.dance]) e.addEventListener('pointerdown', (ev) => ev.stopPropagation());
    const hud = $('#hud');
    hud.append(this.panel, this.track, this.dance);
    cardMenuButton('mbSect', 'สำนัก', undefined, () => this.toggle());
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }
  close(): void {
    this.panel.hidden = true;
  }
  toggle(): void {
    this.panel.hidden = !this.panel.hidden;
    this.sig = '';
    if (!this.panel.hidden) this.render();
  }

  /** A senior's talk (dialog with a school id) opens the activity menu for members. */
  handle(ev: WorldEvent): void {
    if (ev.type === 'dialog' && ev.schoolId && this.world.progress.school === ev.schoolId) {
      this.panel.hidden = false;
      this.sig = '';
      this.render();
    }
  }

  private sys(): { hud(): Hud | null } {
    return this.world.sys.sectActivities;
  }
  private goods(): Record<string, number> {
    return this.world.progress.trades.goods;
  }

  private render(): void {
    const mine = this.world.progress.school ?? '';
    const g = this.goods();
    if (!this.target || this.target === mine) this.target = SECT_TABLE.find((s) => s.id !== mine)?.id ?? '';
    const act = (k: SectActKind, label: string) => {
      const b = h('button', { type: 'button' }, label);
      b.addEventListener('click', () => this.send({ t: 'sectAct', kind: k }));
      return b;
    };
    const sel = h('select');
    for (const s of SECT_TABLE) if (s.id !== mine) sel.append(h('option', { value: s.id, selected: s.id === this.target }, s.name));
    sel.addEventListener('change', () => (this.target = sel.value));
    const spy = h('button', { type: 'button' }, `${SECT_ACT_NAME.spy}…`);
    spy.addEventListener('click', () => {
      const here = this.world.progress.location as string;
      if (here !== `sect-${this.target}`) { spy.textContent = 'ไปแผนที่สำนักนั้นก่อน'; return; }
      this.send({ t: 'sectAct', kind: 'spy' });
    });
    const close = cardClose(() => this.close());
    const books = h('div');
    for (const b of SECT_BOOKS) {
      let n = 0;
      for (let i = 1; i <= b.pageCount; i++) if ((g[pageId(b.id, i)] ?? 0) > 0) n++;
      const have = g[bookGoodId(b.id)] ?? 0;
      if (!n && !have) continue;
      const bind = h('button', { type: 'button', disabled: n < b.pageCount }, 'รวมเล่ม');
      bind.addEventListener('click', () => this.send({ t: 'sectBind', book: b.id }));
      const read = h('button', { type: 'button', disabled: have < 1 }, 'อ่านเรียน');
      read.addEventListener('click', () => this.send({ t: 'sectRead', book: b.id }));
      books.append(h('div', { class: 'row' }, h('span', {}, `${b.name} ${n}/${b.pageCount}${have ? ` (เล่ม ×${have})` : ''}`), bind, read));
    }
    if (!books.childNodes.length) books.append(h('div', {}, 'ยังไม่มีหน้าตำรา'));
    this.panel.replaceChildren(
      h('h3', {}, 'กิจการสำนัก', close),
      h('div', { class: 'row' }, act('patrol', SECT_ACT_NAME.patrol), act('hunt', SECT_ACT_NAME.hunt)),
      h('div', { class: 'row' }, sel, spy),
      h('h4', {}, 'หน้าตำรา'),
      books,
    );
  }

  sync(dt: number): void {
    // Tracker card.
    const r = this.sys().hud();
    if (!r) this.track.hidden = true;
    else {
      this.track.hidden = false;
      const txt =
        r.kind === 'hunt'
          ? `${SECT_ACT_NAME.hunt}: ผู้สอดแนมเหลือ ${r.foes}`
          : r.kind === 'patrol'
            ? `${SECT_ACT_NAME.patrol}: จุด ${r.next}/${r.total}${r.foes ? ` · ผู้บุกรุก ${r.foes}` : ''}`
            : `${SECT_ACT_NAME.spy}: จุดสอดแนม ${r.next}/${r.total}${r.caught ? ' · ถูกจับได้! หนี!' : ' · ระวังยาม'}`;
      if (this.track.textContent !== txt) this.track.textContent = txt;
    }
    if ((this.t += dt) < 0.5) return;
    this.t = 0;
    // Sect dance prompt.
    const id = this.world.progress.school;
    const open =
      !!id &&
      (this.world.progress.location as string) === `sect-${id}` &&
      !this.world.dance.me() &&
      sectDanceState(id, Date.now()).phase === 'open';
    this.dance.hidden = !open;
    void SECT_DANCE;
    // Refresh the panel when the bag changes.
    if (this.isOpen) {
      const g = this.goods();
      const sig = Object.keys(g).filter((k) => k.startsWith('page:') || k.startsWith('book:')).map((k) => `${k}${g[k]}`).join();
      if (sig !== this.sig) {
        this.sig = sig;
        this.render();
      }
    }
  }
}
